import { isPlainObject } from "is-plain-object";
import { CountryCode, ParsedNumber, parseNumber, parsePhoneNumber } from "libphonenumber-js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { FormattedPhoneBookUser, PhoneBookUser, StringOrNumber, UserDetailsType, UserFriendType } from "../../types";
import { MIN_AGE } from "../../config";
import { LocalStorageKeys, setToLocalStorage } from "./localStorage";
import { reportError } from "./reportError";

dayjs.extend(utc);

export const isMobile = (() => {
  if (typeof navigator === "undefined" || typeof navigator.userAgent !== "string") {
    return false;
  }

  return /Mobile/.test(navigator.userAgent);
})();

// Recursively removes any object keys with a value of undefined
export function removeUndefineds<T>(obj: T): T {
  if (!isPlainObject(obj)) return obj;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const target: { [name: string]: any } = {};

  for (const key in obj) {
    if (typeof obj[key] !== "undefined") {
      target[key] = removeUndefineds(obj[key]);
    }
  }

  return target as T;
}

export async function getDeviceInfo() {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return {
    audioInputDevices: devices.filter((device) => device.kind === "audioinput"),
    videoInputDevices: devices.filter((device) => device.kind === "videoinput"),
    audioOutputDevices: devices.filter((device) => device.kind === "audiooutput"),
    hasAudioInputDevices: devices.some((device) => device.kind === "audioinput"),
    hasVideoInputDevices: devices.some((device) => device.kind === "videoinput"),
  };
}

// This function will return 'true' when the specified permission has been denied by the user.
// If the API doesn't exist, or the query function returns an error, 'false' will be returned.
export async function isPermissionDenied(name: PermissionName) {
  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({ name });
      return result.state === "denied";
    } catch {
      return false;
    }
  } else {
    return false;
  }
}

/**
 * Returns the referrer if it exists
 */
export const getReferrerParam = () => {
  const { referrer } = document;

  return referrer ? `&referr_url=${encodeToBase64(referrer)}` : "";
};

export const saveUtmDataParams = (search: string) => {
  const searchParams = new URLSearchParams(search);
  const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

  const utmData = utmKeys.reduce((acc, key) => {
    const value = searchParams.get(key);

    return value ? `${acc}&${key}=${encodeURIComponent(value)}` : acc;
  }, "");

  if (utmData) {
    setToLocalStorage(LocalStorageKeys.UtmData, utmData);
  }
};

export const saveReferrerParam = (search: string) => {
  const searchParams = new URLSearchParams(search);
  const referrer = searchParams.get("referr_url");

  if (referrer) {
    setToLocalStorage(LocalStorageKeys.referrer, `&referr_url=${encodeToBase64(referrer)}`);
  }
};

export const parseJWT = (token: string) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (e) {
    return null;
  }
};

export const getBackgroundColor = (isChecked = false, isDisabled = false, isInvalid = false) => {
  let defaultBackground = "transparent";

  if (isChecked) defaultBackground = "info";

  if (isInvalid && isChecked) defaultBackground = "error";

  if (isDisabled) defaultBackground = "blueGray";

  return defaultBackground;
};

export const getBorderColor = (isChecked = false, isDisabled = false, isInvalid = false) => {
  let defaultBorderColor = "blueGray";

  if (isChecked) defaultBorderColor = "info";

  if (isInvalid) defaultBorderColor = "error";

  if (isDisabled) defaultBorderColor = "blueGray";

  return defaultBorderColor;
};

export const convertToString = (dateInMs: number) => {
  const date = new Date(dateInMs);

  const formatDate = dayjs(date).utc().format("MM/DD/YYYY");

  return formatDate;
};

export const emailRE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const nameRE = /^[a-zA-Z ]*$/;

export const dateMlRE = /[^0-9.]/g;

export const displayDateRE = /^(0[1-9]|1[0-2]|1\d|2\d|3[01])\/(0[1-9]|1\d|2\d|3[01]|1[0-2])\/(19|20)\d{2}$/;

export const isValidAge = (birthday: StringOrNumber, isUSA = true) => {
  if (!birthday) return false;

  let dob = birthday;

  if (!isUSA) {
    const array = dob.toString().split("/");
    dob = `${array[1]}/${array[0]}/${array[2]}`;
  }

  const date = dayjs(dob);
  const today = dayjs();

  return today.diff(date, "year") >= MIN_AGE;
};

export const formatExpressionValue = (value: number) => Number(value.toFixed(5));

export function encodeToBase64(string: string) {
  return Buffer.from(string).toString("base64");
}

export const UnitedStates = "United States";

export function formatNumberWithCommas(number: number) {
  return new Intl.NumberFormat("en-US").format(number);
}

export const getRandomPosition = () => Math.ceil(Math.random() * 2_000) + 1_000;

export function formatPhoneBookUsers(users: PhoneBookUser[], countryCode: string): FormattedPhoneBookUser[] {
  return users
    .map((user) => {
      try {
        const parsed = parsePhoneNumber(user.tel[0], countryCode as CountryCode);
        const metadata = parseNumber(parsed.number, {
          extended: true,
        });

        if ((metadata as ParsedNumber)?.valid) {
          return {
            name: user.name[0],
            phoneNumber: parsed.number,
          };
        }
      } catch (e) {
        reportError("utils/index.ts - formatPhoneBookUsers.catch", "Format Phone Book Users Error", e as Error);
      }

      return {
        name: user.name[0],
        phoneNumber: "",
      };
    })
    .filter((user) => !!user.phoneNumber);
}

export const getFullName = (details: UserDetailsType) => {
  if (details?.firstName || details?.lastName) {
    return `${details?.firstName || ""} ${details?.lastName || ""}`.trim();
  }

  return `User#${details?.userId}`;
};

export const formatDashedStringToCamelCase = (inputString: string) =>
  inputString
    .split("-")
    .map((word, index) => {
      if (index === 0) return word;

      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join("");

export const getFilteredFriendsList = (list: UserFriendType[], searchQuery: string) =>
  list.filter((friend) => {
    const { firstName, lastName } = friend.friendDetails;
    const fullname = `${firstName} ${lastName}`.toLocaleLowerCase();

    return fullname.includes(searchQuery.toLocaleLowerCase());
  });
